import React, { useRef, useState, useEffect } from 'react';
import { FormControl, TextField } from '@mui/material';
import PropTypes from 'prop-types';
import GoogleWrapper from '../google-wrapper/google-wrapper.component';

const PlaceAutocomplete = ({ onPlaceSelect, textFieldProps, address }) => {
    const [addressData, setAddressData] = useState('');
    const inputRef = useRef(null);
    const autoCompleteRef = useRef(null);
    const isClickedGASuggest = useRef(false);
    const selectedAddress = useRef(null);

    const handleChange = e => {
        isClickedGASuggest.current = false;
        selectedAddress.current = null;
        setAddressData(e.target.value);
        // When field is empty, let parent know that its empty (valuable for validation)
        if (!e.target.value) {
            onPlaceSelect('');
        }
    };

    // If not selected from GA, then clear the text field, as we collect the coordinates
    const handleBlur = () => {
        setTimeout(() => {
            if (!isClickedGASuggest.current && !selectedAddress.current) {
                setAddressData('');
                onPlaceSelect('');
            }
        }, 200);
    };

    // Auto populate the data when passed state from parent
    useEffect(() => {
      if (!address) {
        return;
      }

      setAddressData(address);
      // To handle/stop infinite useffect loop(as we do not need suggestion here --> passed from parent)
      isClickedGASuggest.current = null;
    }, [address]);

    // Send to parent, only when selected from suggestions
    useEffect(() => {
        if (!isClickedGASuggest.current) return;
        onPlaceSelect(selectedAddress.current);
    }, [addressData, onPlaceSelect]);

    // Initiate Google Autocomplete
    useEffect(() => {
        const placeChangeListener = () => {
            selectedAddress.current = autoCompleteRef.current.getPlace();
            const { formatted_address: formattedAddress } = selectedAddress.current;
            isClickedGASuggest.current = true;
            setAddressData(formattedAddress);
        };
        const initAutoComplete = async () => {
            if (!autoCompleteRef.current) {
                const { Autocomplete } = await window.google.maps.importLibrary('places');
                const options = {
                    fields: ['geometry', 'name', 'formatted_address', 'place_id', 'address_components', 'adr_address'],
                    componentRestrictions: { country: ['in'] },
                };
                autoCompleteRef.current = new Autocomplete(inputRef.current, options);
                window.google.maps.event.addListener(autoCompleteRef.current, 'place_changed', placeChangeListener);
            }
        };

        if (!inputRef.current) return;

        initAutoComplete();

        return () => {
            window.google.maps.event.clearListeners(autoCompleteRef.current, 'place_changed');
        };
    }, []);

    return (
        <FormControl fullWidth>
            <TextField
                fullWidth
                variant="outlined"
                value={addressData}
                onChange={handleChange}
                inputRef={inputRef}
                {...textFieldProps}
                onBlur={handleBlur}
            />
        </FormControl>
    );
};

PlaceAutocomplete.propTypes = {
    onPlaceSelect: PropTypes.func,
    textFieldProps: PropTypes.object,
    address: PropTypes.string,
};

const GoogleAutocomplete = ({ setAddress, address = '', textFieldProps }) => {
    return (
        <GoogleWrapper>
            <PlaceAutocomplete
                onPlaceSelect={setAddress}
                textFieldProps={textFieldProps}
                address={address}
            />
        </GoogleWrapper>
    );
};

export default GoogleAutocomplete;

GoogleAutocomplete.propTypes = {
    address: PropTypes.string,
    setAddress: PropTypes.func.isRequired,
    textFieldProps: PropTypes.object,
};